import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

/**
 * Nobody can currently say whether this tenant's cluster holds orphaned build
 * jobs: its agent has not reported a build-orphan census within the census's
 * freshness window.
 *
 * SEPARATE FROM `VIBE_BUILD_ORPHANS_PERSISTENT`, and the two partition cleanly —
 * the same split as the region-quota pair. That one says we looked and found
 * orphans; this one says we could not look. An orphan alarm already open is
 * HELD while this one stands, because retracting it is a claim ("I looked and
 * it cleared") an absent census does not support — and a held alarm with nothing
 * beside it reads exactly like a freshly measured one.
 *
 * Expected on a tenant whose agent predates the census until that agent is
 * upgraded; anywhere else it means the agent is silent or its Nomad job list is
 * failing.
 */
export const VIBE_BUILD_ORPHAN_CENSUS_UNOBSERVED = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "This serving tenant's agent has not reported a build-orphan census recently, so nobody can say whether a build the control plane gave up on is still pending or running on its cluster. An existing orphan alarm for this tenant is being held, not retracted, until a census arrives. Nothing is known to be wrong with the builds themselves — what is missing is the ability to look.",
  severity: "medium",
  remedy:
    "Check the tenant agent's version on the admin tenant-cluster view: an agent older than the build-orphan census never reports one, and the fix is the agent upgrade. A current agent that reports nothing is either silent (its heartbeat has stopped — see the agent-liveness metrics) or failing to list its Nomad build jobs (its `reconcile: build orphan sweep skipped` log lines name the error). This clears on its own on the first census that arrives."
} as const satisfies IssueDefinition;
